<template>
	<Modal
		max-width="1000px"
		show-search-field
		:search-query.sync="searchQuery"
		search-query-label="Integration Templates"
	>
		<template #modal-title>
			<IconButton
				class="mr-3"
				data-testid="back-button"
				@click="$emit('back')"
			>
				mdi-arrow-left
			</IconButton>
			<span class="title">
				Choose Template
			</span>
			<v-spacer />
			<IconButton
				data-testid="close-button"
				@click="$emit('close')"
			>
				mdi-close
			</IconButton>
		</template>

		<template #modal-content>
			<div
				class="template-selection"
				:class="{ 'with-search': templates.length > 3 }"
			>
				<div
					v-if="filteredTemplates.length === 0 && templates.length > 0"
					class="no-results-message"
				>
					<div class="empty-state">
						<div class="empty-state-icon">
							<v-icon
								size="64"
								color="grey lighten-2"
							>
								mdi-file-search-outline
							</v-icon>
						</div>
						<div class="empty-state-content">
							<h3 class="empty-state-title">
								No templates found
							</h3>
							<p class="empty-state-description">
								We couldn't find any templates matching your search. Try different keywords or browse all templates.
							</p>
							<div class="empty-state-actions">
								<v-btn
									outlined
									color="primary"
									small
									class="mr-2"
									@click="searchQuery = ''"
								>
									Clear search
								</v-btn>
								<v-btn
									text
									color="primary"
									small
									@click="$emit('select-custom')"
								>
									Create custom
								</v-btn>
							</div>
						</div>
					</div>
				</div>

				<div
					v-else
					class="templates-grid"
				>
					<SystemTemplateCard
						v-for="template in filteredTemplates"
						:key="template.id"
						:template="template"
						:selected="selectedTemplateId === template.id"
						:data-testid="`template-${template.id}`"
						@select="selectTemplate(template)"
					/>
				</div>
			</div>
			<div class="footer-content mt-2">
				<!-- Pagination -->
				<div
					v-if="showPagination"
					class="pagination-section"
				>
					<div class="footer-row">
						<div class="spacer" />
						<div class="pagination-wrapper">
							<!-- Custom dot pagination -->
							<div class="dot-pagination">
								<button
									class="nav-arrow"
									:disabled="currentPage === 1"
									@click="currentPage--"
								>
									<v-icon small>
										mdi-chevron-left
									</v-icon>
								</button>

								<div class="dots-container">
									<button
										v-for="page in totalPages"
										:key="page"
										class="page-dot"
										:class="{ active: page === currentPage }"
										@click="currentPage = page"
									/>
								</div>

								<button
									class="nav-arrow"
									:disabled="currentPage === totalPages"
									@click="currentPage++"
								>
									<v-icon small>
										mdi-chevron-right
									</v-icon>
								</button>
							</div>
						</div>
						<div class="custom-setup-link">
							Can't find what you're looking for? Try <a
								class="primary--text"
								@click="$emit('select-custom')"
							>
								custom setup
							</a>
						</div>
					</div>
				</div>				<div
					v-if="!showPagination"
					class="footer-bottom"
				>
					<div class="spacer" />
					<div class="custom-setup-link">
						Can't find what you're looking for? Try <a
							class="primary--text"
							@click="$emit('select-custom')"
						>
							custom setup
						</a>
					</div>
				</div>
			</div>
		</template>

		<template #modal-footer />
	</Modal>
</template>

<script>
import Modal from '../../../../../shared/components/modal.vue'
import IconButton from '../../../../../shared/components/icon-button.vue'
import SystemTemplateCard from './system-template-card.vue'
import { SYSTEM_TEMPLATES } from '../../../../../shared/lib/system-integration-templates.js'

export default {
	name: 'template-selection-modal',
	components: {
		Modal,
		IconButton,
		SystemTemplateCard
	},
	data () {
		return {
			templates: SYSTEM_TEMPLATES,
			selectedTemplateId: null,
			searchQuery: '',
			currentPage: 1,
			itemsPerPage: 6 // 3 columns x 2 rows
		}
	},
	computed: {
		allFilteredTemplates () {
			if (!this.searchQuery || this.searchQuery.trim() === '') {
				return this.templates
			}

			const query = this.searchQuery.toLowerCase().trim()
			return this.templates.filter(template => {
				// Search in template name
				if (template.name && template.name.toLowerCase().includes(query)) {
					return true
				}

				// Search in template description
				if (template.description && template.description.toLowerCase().includes(query)) {
					return true
				}
				// Search in template category
				if (template.category && template.category.toLowerCase().includes(query)) {
					return true
				}
				// Search in template features
				if (template.features && template.features.some(feature =>
					feature.toLowerCase().includes(query)
				)) {
					return true
				}

				return false
			})
		},
		filteredTemplates () {
			const start = (this.currentPage - 1) * this.itemsPerPage
			const end = start + this.itemsPerPage
			return this.allFilteredTemplates.slice(start, end)
		},
		totalPages () {
			return Math.ceil(this.allFilteredTemplates.length / this.itemsPerPage)
		},
		showPagination () {
			return this.allFilteredTemplates.length > this.itemsPerPage
		}
	},
	watch: {
		searchQuery () {
			// Reset to first page when search changes
			this.currentPage = 1
		}
	},
	methods: {
		selectTemplate (template) {
			this.selectedTemplateId = template.id
			// Emit selection after a brief delay to show selection state
			setTimeout(() => {
				this.$emit('template-selected', template)
			}, 200)
		},
		goToPage (page) {
			if (page >= 1 && page <= this.totalPages) {
				this.currentPage = page
			}
		},
		goToPreviousPage () {
			if (this.currentPage > 1) {
				this.currentPage--
			}
		},
		goToNextPage () {
			if (this.currentPage < this.totalPages) {
				this.currentPage++
			}
		}
	}
}
</script>

<style scoped>
.template-selection {
  min-height: 400px;
}

.template-selection.with-search {
  padding-top: 0;
}

.search-container {
  position: sticky;
  top: -24px;
  z-index: 10;
  background-color: white;
  padding: 24px 24px 16px 24px;
  margin: -24px -24px 16px -24px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.search-field {
  max-width: 100%;
}

.templates-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  max-width: 1232px; /* 3 * 400px + 2 * 16px gap */
  margin: 0 auto;
  gap: 16px;
  min-height: 400px; /* Fixed height for 2 rows to prevent modal jumping */
  align-content: start;
}

@media (min-width: 1200px) {
  .templates-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

.no-results-message {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  padding: 40px 20px;
}

.empty-state {
  text-align: center;
  max-width: 400px;
}

.empty-state-icon {
  margin-bottom: 24px;
  opacity: 0.7;
}

.empty-state-content {
  color: rgba(0, 0, 0, 0.87);
}

.empty-state-title {
  font-size: 1.25rem;
  font-weight: 500;
  margin-bottom: 8px;
  color: rgba(0, 0, 0, 0.87);
}

.empty-state-description {
  font-size: 0.875rem;
  line-height: 1.5;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 24px;
}

.empty-state-actions {
  display: flex;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .templates-grid {
    grid-template-columns: 1fr;
    gap: 12px;
    max-width: 100%;
    min-height: 600px; /* Taller for single column */
  }
}

@media (max-width: 1199px) and (min-width: 769px) {
  .templates-grid {
    grid-template-columns: repeat(2, 1fr);
	min-height: 500px; /* Medium height for two columns */
	/* Constrain to two-card width + gap and center the grid inside the modal */
	max-width: calc(2 * 400px + 1 * 16px);
	margin-left: auto;
	margin-right: auto;
	justify-content: center;
  }
}

/* Footer styles */
.footer-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.pagination-section {
  width: 100%;
}

.pagination-info {
  text-align: center;
  margin-top: 12px;
}

.pagination-info-row {
  display: flex;
  align-items: center;
  margin-top: 12px;
  width: 100%;
  position: relative;
}

.pagination-info-row span {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  text-align: center;
}

.footer-bottom-row {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 8px;
  width: 100%;
}

.footer-spacer {
  flex-grow: 1;
  min-height: 20px;
}

.footer-row {
  display: flex;
  align-items: center;
  width: 100%;
  position: relative;
  min-height: 40px;
}

.pagination-wrapper {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  justify-content: center;
  z-index: 1;
}

/* Custom dot pagination */
.dot-pagination {
  display: flex;
  align-items: center;
  gap: 12px;
}

.nav-arrow {
  background: none;
  border: none;
  padding: 4px;
  border-radius: var(--radius-full, 9999px);
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: rgba(0, 0, 0, 0.54);
  transition: all 0.2s ease;
}

.nav-arrow:hover:not(:disabled) {
  background-color: rgba(0, 0, 0, 0.04);
  color: rgba(0, 0, 0, 0.87);
}

.nav-arrow:disabled {
  color: rgba(0, 0, 0, 0.26);
  cursor: not-allowed;
}

.dots-container {
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-dot {
  width: 8px;
  height: 8px;
  border-radius: var(--radius-full, 9999px);
  border: none;
  background-color: rgba(0, 0, 0, 0.26);
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;
}

.page-dot:hover {
	background-color: rgba(0, 0, 0, 0.54);
  transform: scale(1.2);
}

.page-dot.active {
	background-color: rgba(0, 0, 0, 0.60);
  transform: scale(1.1);
}

.footer-bottom {
  display: flex;
  justify-content: flex-end;
  width: 100%;
  align-items: flex-end;
  margin-top: 12px;
}

.spacer {
  flex: 1;
}

.custom-setup-link {
  color: rgba(0, 0, 0, 0.6);
  font-size: 0.75rem;
  white-space: nowrap;
  align-self: center;
  margin-left: auto;
}
</style>
